import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActionIcon, Alert, Button, Collapse, Select, TextInput, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  REQ_ALL,
  buildTemplate,
  can,
  computeChecks,
  formatDay,
  formatInt,
  formatMoney,
  headerFieldTexts,
  inputColumns,
  isRequired,
  modeInstructions,
  normalizeArticle,
  numOrNull,
  roleTitle,
  selectableRequests,
  selectedRequestKey,
  templateReasonText,
  visibleColumns,
  type CommitResponse,
  type InputKey,
  type PreviewResponse,
  type PreviewView,
  type PurchaseHeader,
  type PurchaseMode,
  type PurchaseRow,
  type RefKey,
  type Snapshot
} from '@parts/shared';
import { errorText, postJson } from '../../api';
import { PageHeader } from '../../components/layout/PageHeader';
import { FieldLabel, Metric } from '../../components/ui';
import { IconArrowDownLine, IconCircleCheck, IconDownload, IconEraser, IconHelp, IconUpload } from '../../icons';
import { EntryState, RequestFacts } from '../EntryParts';
import { DayInput } from '../../components/DayInput';
import { InputGrid, type InputGridColumn, type InputGridHandle } from '../../components/table/InputGrid';
import { StockImportDialog } from '../../excel/StockImportDialog';
import { t as tt, useI18n } from '../../i18n';
import { useRole } from '../../role';
import { useAppState } from '../../state';
import { PreviewModal } from './PreviewModal';
import classes from './PurchaseScreen.module.css';

/** «Закупка» — одна таблица: наличие, цена и «Закупить» в одной строке. */
const EMPTY_HEADER: PurchaseHeader = { mode: 'purchase', reqSel: '', date: '', supplier: '', invoice: '', comment: '' };

/**
 * Экран «Закупка»: наличие и цены фабрики либо закупка по выбранному запросу.
 * Таблицу для фабрики можно выгрузить в Excel и загрузить заполненной обратно.
 */
export function PurchaseScreen() {
  const { lang, t, tx } = useI18n();
  const role = useRole();
  const { state, error: loadError, reload, apply } = useAppState();
  const [header, setHeader] = useState<PurchaseHeader>(EMPTY_HEADER);
  const [showHelp, setShowHelp] = useState(false);
  const [preview, setPreview] = useState<{ view: PreviewView; version: number } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [posted, setPosted] = useState<{ title: string; message: string } | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  /** Счётчик изменений в таблице — по нему пересчитываются итоги. */
  const [tick, setTick] = useState(0);
  const grid = useRef<InputGridHandle | null>(null);

  const snap = state?.snapshot ?? null;
  const mode = header.mode;
  const canPost = can(role, mode === 'packing' ? 'POST_PACKING' : 'POST_PURCHASE');

  useEffect(() => {
    setPreview(null);
  }, [snap?.version]);

  const rowsNow = useCallback((): PurchaseRow[] => (grid.current?.rows() ?? []) as PurchaseRow[], []);

  const checks = useMemo(() => {
    if (!snap) return { checks: [], refs: [], rows: 0, errors: 0 };
    void tick;
    return computeChecks(header, rowsNow(), snap);
  }, [header, snap, tick, rowsNow]);

  /** Проверка всей таблицы сразу — для столбца «Проверка» (повтор OEM виден только между строками). */
  const checkRow = useCallback(
    (rows: Record<string, string>[]) => (snap ? computeChecks(header, rows as PurchaseRow[], snap).checks : rows.map(() => null)),
    [header, snap]
  );

  const refValue = useCallback(
    (row: Record<string, string>, key: RefKey): string => {
      if (!snap) return '';
      const art = normalizeArticle(row.art);
      if (!art) return '';
      const refs = computeChecks(header, [row as PurchaseRow], snap).refs[0] ?? {};
      const v = refs[key];
      if (v === undefined) return '';
      if (v === null) return '—';
      return typeof v === 'number' ? formatInt(v) : String(v);
    },
    [header, snap]
  );

  const gridColumns = useMemo<InputGridColumn[]>(
    () =>
      visibleColumns(mode).map((col) => ({
        key: col.key,
        title: tx(col.title),
        width: col.width,
        ref: col.ref,
        required: !col.ref && isRequired(col, mode) && !col.soft,
        align: col.format === 'text' ? 'left' : 'right',
        value: col.ref ? (row) => refValue(row, col.key as RefKey) : undefined
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, refValue, lang]
  );

  const loadTemplate = useCallback(
    (h: PurchaseHeader, s: Snapshot, quiet = false) => {
      const res = buildTemplate(h, s);
      grid.current?.setRows(res.rows as Record<string, string>[]);
      if (!quiet && res.reason !== 'noselect') {
        notifications.show({
          color: res.reason === 'ok' ? 'accent' : 'yellow',
          message: tx(templateReasonText(h.mode, res.reason, res.rows.length)),
          autoClose: 3000
        });
      }
    },
    []
  );

  const changeRequest = (value: string | null) => {
    const next: PurchaseHeader = { ...header, reqSel: value ?? '' };
    setHeader(next);
    if (snap) loadTemplate(next, snap);
  };

  /** Ошибки в таблице: номера строк в уведомлении, таблица прокручивается к первой из них. */
  const showRowErrors = (count: number) => {
    const nums = grid.current?.errorRows() ?? [];
    notifications.show({
      color: 'red',
      title: t('Строк с ошибками: {n}', { n: count }),
      message: nums.length
        ? t('Строки {rows}. Причина — в столбце «Проверка» этих строк.', { rows: nums.join(', ') })
        : t('Причина — в столбце «Проверка».'),
      autoClose: 8000
    });
    grid.current?.showFirstError();
  };

  const openPreview = async () => {
    const rows = rowsNow();
    if (!snap) return;
    const res = computeChecks(header, rows, snap);
    if (res.rows === 0) {
      notifications.show({
        color: 'yellow',
        message: t('Нет изменений для проведения: обновите наличие или цену либо заполните «Закупить».')
      });
      return;
    }
    if (res.errors > 0) {
      showRowErrors(res.errors);
      return;
    }
    setPreviewLoading(true);
    try {
      const answer = await postJson<PreviewResponse>('/api/purchase/preview', { header, rows });
      if (answer.ok) setPreview({ view: answer.view, version: answer.version });
      else notifications.show({ color: 'red', title: t('Нельзя провести'), message: tx(answer.message) });
    } catch (err) {
      notifications.show({ color: 'red', title: t('Ошибка'), message: tx(errorText(err)) });
    } finally {
      setPreviewLoading(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const res = await postJson<CommitResponse>('/api/purchase/commit', {
        header,
        rows: rowsNow(),
        version: preview.version
      });
      if (!res.ok) {
        notifications.show({ color: 'red', title: t('Не проведено'), message: tx(res.message) });
        if (res.message.includes('изменились')) {
          setPreview(null);
          void reload();
        }
        return;
      }
      const title = preview.view.title;
      setPreview(null);
      apply(res.state);
      notifications.show({ color: 'green', title: t('Проведено: {title}', { title: tx(title) }), message: tx(res.message), autoClose: 8000 });
      setPosted({ title, message: res.message });
      const s = res.state.snapshot;
      const stillSelectable = header.reqSel === REQ_ALL || selectableRequests(s, mode).some((r) => r.no === header.reqSel);
      const next = stillSelectable ? header : { ...header, reqSel: '' };
      setHeader(next);
      loadTemplate(next, s, true);
    } catch (err) {
      notifications.show({ color: 'red', title: t('Ошибка'), message: tx(errorText(err)) });
    } finally {
      setCommitting(false);
    }
  };

  const header_ = (
    <PageHeader
      title={t('Закупка')}
      zoom
      subtitle={t('наличие и цены фабрики, закупка по запросам')}
    />
  );

  if (loadError || !snap || !state)
    return (
      <div className={classes.page}>
        {header_}
        <EntryState error={loadError} onRetry={() => void reload()} />
      </div>
    );

  const texts = headerFieldTexts(mode);
  const requests = selectableRequests(snap, mode);
  const selected = snap.reqs.find((r) => r.key === selectedRequestKey(header)) ?? null;
  const totals = computeTotals(mode, rowsNow(), header, snap);
  const exportUrl = `/api/export/stock.xlsx?req=${encodeURIComponent(header.reqSel || REQ_ALL)}`;

  return (
    <div className={classes.page}>
      {header_}

      <div className={classes.body}>
        {posted && (
          <Alert color="go" variant="light" withCloseButton onClose={() => setPosted(null)} title={t('Проведено: {title}', { title: tx(posted.title) })}>
            {tx(posted.message)} {t('Данные пересчитаны.')}
          </Alert>
        )}
        {!canPost && (
          <Alert color="yellow" variant="light" py="xs">
            {t('Роль «{role}» может заполнять форму, но не проводить. Права выдаёт администратор.', { role: tx(roleTitle(role)) })}
          </Alert>
        )}

        <section className={classes.form}>
          <div className={classes.fields}>
            <Select
              label={<FieldLabel text={tx(texts.reqSel.label)} hint={tx(texts.reqSel.hint)} />}
              placeholder={t('Выберите запрос')}
              value={header.reqSel || null}
              onChange={changeRequest}
              data={[{ value: REQ_ALL, label: t('Все открытые запросы') }, ...requests.map((r) => ({ value: r.no, label: r.no }))]}
              searchable
              nothingFoundMessage={t('Не найдено')}
            />
            <DayInput
              label={<FieldLabel text={tx(texts.date.label)} hint={tx(texts.date.hint)} />}
              placeholder={t('Сегодня, {date}', { date: formatDay(snap.today) })}
              value={header.date}
              onChange={(v) => setHeader({ ...header, date: v })}
            />
            <TextInput
              label={<FieldLabel text={tx(texts.supplier.label)} hint={tx(texts.supplier.hint)} />}
              value={header.supplier}
              onChange={(e) => setHeader({ ...header, supplier: e.target.value })}
            />
            <TextInput
              label={<FieldLabel text={tx(texts.invoice.label)} hint={tx(texts.invoice.hint)} />}
              value={header.invoice}
              onChange={(e) => setHeader({ ...header, invoice: e.target.value })}
            />
            <TextInput
              label={<FieldLabel text={tx(texts.comment.label)} hint={tx(texts.comment.hint)} />}
              value={header.comment}
              onChange={(e) => setHeader({ ...header, comment: e.target.value })}
            />
          </div>

          <div className={classes.infoLine}>
            {selected ? (
              <RequestFacts req={selected} />
            ) : (
              <span className={classes.infoMuted}>
                {header.reqSel === REQ_ALL
                  ? mode === 'purchase'
                    ? t('Закупка распределится по открытым запросам по очереди, от старых к новым.')
                    : t('Все OEM, которые ещё нужно закупить по открытым запросам.')
                  : t('Запрос не выбран — выберите номер, чтобы подставить OEM.')}
              </span>
            )}
          </div>
        </section>

        <div className={classes.toolbar}>
          <div className={classes.tools}>
            <Button
              variant="default"
              leftSection={<IconArrowDownLine size={15} />}
              onClick={() => snap && loadTemplate(header, snap)}
              disabled={!header.reqSel}
            >
              {t('Загрузить OEM запроса')}
            </Button>
            {mode === 'purchase' && (
              <>
                <Button variant="default" leftSection={<IconDownload size={15} />} component="a" href={exportUrl} download>
                  {t('Выгрузить для фабрики')}
                </Button>
                <Button variant="default" leftSection={<IconUpload size={15} />} onClick={() => setImportOpen(true)}>
                  {t('Загрузить ответ фабрики')}
                </Button>
              </>
            )}
            <Tooltip label={t('Очистить таблицу')}>
              <ActionIcon variant="subtle" color="gray" size={36} onClick={() => grid.current?.setRows([])} aria-label={t('Очистить таблицу')}>
                <IconEraser size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={showHelp ? t('Скрыть инструкцию') : t('Как заполнять')}>
              <ActionIcon
                variant={showHelp ? 'light' : 'subtle'}
                color={showHelp ? 'accent' : 'gray'}
                size={36}
                onClick={() => setShowHelp((v) => !v)}
                aria-label={t('Как заполнять')}
              >
                <IconHelp size={16} />
              </ActionIcon>
            </Tooltip>
          </div>
          <div className={classes.summary}>
            <div className={classes.metrics}>
              <Metric label={t('Изменено строк')} value={formatInt(checks.rows)} />
              <Metric label={t('С ошибками')} value={checks.errors} tone={checks.errors ? 'red' : undefined} />
              {totals.map((m) => (
                <Metric key={m.label} label={m.label} value={m.value} />
              ))}
            </div>
            <Button
              color="go"
              className={classes.postButton}
              leftSection={<IconCircleCheck size={17} />}
              onClick={() => void openPreview()}
              loading={previewLoading}
              disabled={!canPost}
            >
              {t('Проверить и провести')}
            </Button>
          </div>
        </div>

        <Collapse expanded={showHelp}>
          <ul className={classes.help}>
            {modeInstructions(mode).map((line) => (
              <li key={line}>{tx(line)}</li>
            ))}
            <li>{t('Таблица работает как в Excel: щелчок выделяет ячейку, двойной щелчок, Enter или начало набора — правка. Ctrl+C и Ctrl+V, выделение мышью, ширина столбцов запоминается.')}</li>
          </ul>
        </Collapse>

        <div className={classes.tableArea}>
          <InputGrid
            gridId={`purchase-${mode}`}
            columns={gridColumns}
            check={checkRow}
            handle={grid}
            onChange={() => setTick((t) => t + 1)}
          />
        </div>
      </div>

      <StockImportDialog
        opened={importOpen}
        onClose={() => setImportOpen(false)}
        knownArticles={snap.known}
        onApply={(rows) => {
          const current = rowsNow();
          const byArt = new Map(current.map((r) => [normalizeArticle(r.art), r]));
          for (const row of rows) {
            const art = normalizeArticle(row.art);
            const found = byArt.get(art);
            if (found) Object.assign(found, row);
            else {
              current.push(row as PurchaseRow);
              byArt.set(art, current[current.length - 1]!);
            }
          }
          grid.current?.setRows(current as Record<string, string>[]);
        }}
      />

      <PreviewModal
        view={preview?.view ?? null}
        committing={committing}
        canCommit={canPost}
        onClose={() => setPreview(null)}
        onCommit={() => void commit()}
      />
    </div>
  );
}

/** Итоги под таблицей для текущего режима (по строкам без ошибок). */
function computeTotals(mode: PurchaseMode, rows: PurchaseRow[], header: PurchaseHeader, snap: Snapshot): { label: string; value: string }[] {
  if (mode !== 'purchase') return [];
  const res = computeChecks(header, rows, snap);
  let qty = 0;
  let amount = 0;
  rows.forEach((r, i) => {
    if (!res.checks[i]?.ok) return;
    const q = numOrNull(r.bought) ?? 0;
    const price = numOrNull(r.price) ?? snap.arts[normalizeArticle(r.art)]?.factoryPrice ?? null;
    qty += q;
    if (price !== null) amount += q * price;
  });
  return [
    { label: tt('Закупить'), value: `${formatInt(qty)} ${tt('шт')}` },
    { label: tt('Сумма'), value: formatMoney(amount) }
  ];
}

export type { InputKey };
